/**
 * Kalender über **alle** Räume — das Gegenstück zu `BookingCalendar`, der
 * genau einen Raum zeigt.
 *
 * Zwei Ansichten, weil ein Raster nicht beides kann:
 *
 * - **Tag**: Räume als Spalten (react-big-calendar `resources`). Hier ist die
 *   Zuordnung Slot → Raum eindeutig, also wird hier gebucht.
 * - **Woche**: alle Räume in einem Raster, Raum über die Farbe. Sieben Tage
 *   mal vier Räume wären 28 Spalten — unbrauchbar. Ein Klick springt deshalb
 *   in die Tagesansicht des angeklickten Tages, statt ins Leere zu laufen.
 */

import { useMemo, useState } from "react";
import {
  Calendar,
  type Event as RbcEvent,
  type SlotInfo,
  type View,
} from "react-big-calendar";
import { startOfWeek } from "date-fns";
import { de } from "date-fns/locale";

import { useCalendarFeed } from "@/hooks/useCalendar";
import {
  calendarFormats,
  calendarMessages,
  eventEnd,
  feedEventDetail,
  localizer,
} from "@/lib/calendar-locale";
import { type EventDetail, EventDetailDialog } from "@/components/EventDetailDialog";
import type { BookingCalendarEvent, Room } from "@/types/api";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./booking-calendar.css";

const messages = calendarMessages("Keine Termine in diesem Zeitraum");
const formats = calendarFormats();

export interface RoomSlot {
  room: Room;
  start: Date;
  end: Date;
}

interface AllRoomsCalendarProps {
  rooms: Room[];
  onSelectSlot?: (slot: RoomSlot) => void;
  selected?: RoomSlot | null;
  date?: Date;
  onNavigate?: (date: Date) => void;
}

type SlotKind = BookingCalendarEvent["kind"] | "selected";

interface RoomCalendarEvent extends RbcEvent {
  kind: SlotKind;
  roomId: number;
  detail?: EventDetail;
}

function rangeForView(view: View, current: Date): { starts_at: string; ends_at: string } {
  const start = new Date(current);
  const end = new Date(current);
  if (view === "month") {
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 1);
  } else if (view === "day") {
    end.setDate(end.getDate() + 1);
  } else {
    const monday = startOfWeek(start, { locale: de });
    start.setTime(monday.getTime());
    end.setTime(monday.getTime());
    end.setDate(end.getDate() + 7);
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return { starts_at: start.toISOString(), ends_at: end.toISOString() };
}

export function AllRoomsCalendar({
  rooms,
  onSelectSlot,
  selected,
  date,
  onNavigate,
}: AllRoomsCalendarProps) {
  const [view, setView] = useState<View>("day");
  const [internalCurrent, setInternalCurrent] = useState<Date>(() => new Date());
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const current = date ?? internalCurrent;
  const setCurrent = (next: Date) => {
    if (onNavigate) onNavigate(next);
    else setInternalCurrent(next);
  };

  const range = useMemo(() => rangeForView(view, current), [view, current]);
  // Ein Abruf für alle Räume — ohne `room_id` liefert der Feed das ganze Haus.
  const { data, isLoading } = useCalendarFeed({
    starts_at: range.starts_at,
    ends_at: range.ends_at,
  });

  const roomById = useMemo(() => new Map(rooms.map((r) => [r.id, r])), [rooms]);
  const isDayView = view === "day";

  const events: RoomCalendarEvent[] = useMemo(() => {
    const label = (e: BookingCalendarEvent) => {
      const fallback = e.kind === "booked" ? "Belegt" : "Geschlossen";
      const name = e.title ?? fallback;
      // In der Wochenansicht fehlt die Raumspalte — dort muss der Raum in den
      // Titel, sonst steht da viermal „Belegt" ohne erkennbaren Bezug.
      if (isDayView) return name;
      return `${roomById.get(e.room_id)?.name ?? "Raum"} · ${name}`;
    };

    const base: RoomCalendarEvent[] = (data ?? []).map((e) => ({
      title: label(e),
      detail: feedEventDetail(
        e,
        e.title ?? (e.kind === "booked" ? "Belegt" : "Geschlossen"),
        roomById.get(e.room_id)?.name,
      ),
      start: new Date(e.starts_at),
      end: eventEnd(e.ends_at),
      kind: e.kind,
      roomId: e.room_id,
    }));

    if (selected) {
      base.push({
        title: `Dein Slot · ${selected.room.name}`,
        start: selected.start,
        end: selected.end,
        kind: "selected",
        roomId: selected.room.id,
      });
    }
    return base;
  }, [data, selected, isDayView, roomById]);

  function handleSelectSlot(slot: SlotInfo) {
    const slotStart = slot.start instanceof Date ? slot.start : new Date(slot.start);

    // Wochenansicht: ohne Raumspalte ist nicht entscheidbar, welcher Raum
    // gemeint ist — also in den Tag springen, statt stumm nichts zu tun.
    if (!isDayView) {
      setView("day");
      setCurrent(slotStart);
      return;
    }
    if (!onSelectSlot) return;

    const room = roomById.get(Number(slot.resourceId));
    if (!room) return;

    let slotEnd = slot.end instanceof Date ? slot.end : new Date(slot.end);
    const minDuration = Math.max(60, room.min_duration_minutes);
    if ((slotEnd.getTime() - slotStart.getTime()) / 60000 < minDuration) {
      slotEnd = new Date(slotStart.getTime() + minDuration * 60000);
    }

    // Nur Termine desselben Raums können kollidieren.
    const conflict = events.some((e) => {
      if (e.kind === "selected" || e.roomId !== room.id) return false;
      if (!e.start || !e.end) return false;
      const eStart = e.start instanceof Date ? e.start : new Date(e.start);
      const eEnd = e.end instanceof Date ? e.end : new Date(e.end);
      return eStart < slotEnd && eEnd > slotStart;
    });
    if (conflict) return;

    onSelectSlot({ room, start: slotStart, end: slotEnd });
  }

  return (
    <div className="rounded-card bg-surface p-4 shadow-card md:p-6">
      {isLoading && (
        <div className="mb-3 text-body-sm text-ink-muted">Kalender wird geladen…</div>
      )}
      {!isDayView && (
        <p className="mb-3 text-body-sm text-ink-muted">
          Wochenüberblick über alle Räume. Zum Buchen einen Tag antippen — die Tagesansicht
          zeigt die Räume nebeneinander.
        </p>
      )}
      {/* Auf schmalen Displays scrollt nur das Raster seitwärts (CSS
          `.all-rooms-day`) — Toolbar und Seite bleiben in Bildschirmbreite. */}
      <div className={isDayView ? "all-rooms-day" : undefined}>
        <div>
          <Calendar
            localizer={localizer}
            culture="de"
            messages={messages}
            formats={formats}
            events={events}
            view={view}
            onView={setView}
            date={current}
            onNavigate={setCurrent}
            views={["day", "week"]}
            // Events über Mitternacht (Partys bis 02:00) ins Raster statt in die Ganztags-Zeile.
            showMultiDayTimes
            step={30}
            timeslots={2}
            min={new Date(1970, 0, 1, 7, 0)}
            max={new Date(1970, 0, 1, 23, 0)}
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={(event) => setDetail((event as RoomCalendarEvent).detail ?? null)}
            // Nur die Tagesansicht bekommt Resources — sonst rendert
            // react-big-calendar sieben Tage × vier Räume nebeneinander.
            resources={isDayView ? rooms : undefined}
            resourceIdAccessor={(resource) => (resource as Room).id}
            resourceTitleAccessor={(resource) => (resource as Room).name}
            // Zwei verschiedene Accessoren, leicht zu verwechseln:
            // `resourceIdAccessor` liest die ID der Spalte, `resourceAccessor`
            // die Spaltenzuordnung eines Termins. Ohne den zweiten landet kein
            // einziger Termin in einer Spalte — die Ansicht bliebe leer.
            resourceAccessor={(event) => (event as RoomCalendarEvent).roomId}
            eventPropGetter={(event) => {
              const { kind, roomId } = event as RoomCalendarEvent;
              return {
                className: `rbc-event--${kind}`,
                // In der Woche trägt die Raumfarbe die Zuordnung. In der
                // Tagesansicht sagt das die Spalte — dort bleibt die
                // Kind-Farbe (belegt/gesperrt) aussagekräftiger.
                style:
                  isDayView || kind === "selected"
                    ? undefined
                    : {
                        borderLeft: `4px solid ${roomById.get(roomId)?.color ?? "#C90050"}`,
                      },
              };
            }}
            style={{ height: 640 }}
          />
        </div>
      </div>

      {!isDayView && (
        <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-body-sm text-ink-soft">
          {rooms.map((room) => (
            <li key={room.id} className="flex items-center gap-2">
              <span
                aria-hidden
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: room.color }}
              />
              {room.name}
            </li>
          ))}
        </ul>
      )}
      <EventDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
